import { observer } from 'mobx-react-lite'
import type { SelectionState } from '@/infra/selection-state'
import { match } from '@/utils/pattern-match-util'
import { InfiniteScrollLoaderContainer } from '@/components/InfiniteScrollLoader/InfiniteScrollLoaderContainer'
import { type StaffingViewModel } from '@/features/admin/staffing/StaffingViewModel'
import { StaffingTable } from '@/features/admin/staffing/components/StaffingTable'
import { StaffingTableRowContainer } from '@/features/admin/staffing/components/StaffingTableRow/StaffingTableRowContainer'
import { ContentLoading } from '@/components/ContentLoading'
import { ContentError } from '@/components/ContentError'

export interface StaffingTableContainerProps {
  vm: StaffingViewModel
}

export const StaffingTableContainer = observer(function StaffingTableContainer({
  vm,
}: StaffingTableContainerProps) {
  const searcher = vm.searcher

  const table = () => (
    <StaffingTable
      columns={vm.columns.map((column) => ({
        id: column.id,
        header: column.header,
        align: column.align,
        canSort: column.sortable,
        sortOrder: vm.getSortOrder(column),
        onSort: (newSort) => {
          vm.setSortOrder(column, newSort)
        },
      }))}
      allSelectedState={match<SelectionState, boolean | 'indeterminate'>(
        searcher.selectionState,
        {
          ['AllSelected']: () => true,
          ['NoneSelected']: () => false,
          ['SomeSelected']: () => 'indeterminate',
        },
      )}
      onToggleAll={searcher.toggleAllPostings}
      loader={
        !searcher.scroller.isDone && (
          <InfiniteScrollLoaderContainer scrollProvider={searcher.scroller} />
        )
      }
    >
      {vm.items.map((itemVm) => (
        <StaffingTableRowContainer
          key={itemVm.workspacePosting.id}
          vm={itemVm}
          columnIds={vm.columnIds}
        />
      ))}
    </StaffingTable>
  )

  return searcher.scroller.fetch.match({
    pending: () => <ContentLoading />,
    rejected: (err) => <ContentError error={err} />,
    resolved: table,
  })
})
